"use client";

import { ArrowRight, Loader2, MapPin, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "~/components/ui/button";
import { safeUUID } from "~/lib/auth/uuid";
import {
  type MapboxSuggestion,
  retrieveAddress,
  suggestAddresses,
} from "~/lib/mapbox/mapbox";
import {
  clearHostAddress,
  writeHostAddress,
} from "~/lib/onboarding/host-address";
import { cn } from "~/lib/utils";

// How long "List now" waits for Mapbox to resolve the pick before continuing
// without the address.
const RETRIEVE_TIMEOUT_MS = 5000;

type Props = {
  /** The background the bar sits on. */
  tone?: "light" | "dark";
  className?: string;
};

/**
 * Address bar on the host landing page (hero and closing section). One pill
 * from `md` up; below it the field and the button stack. The address is
 * optional: a picked Mapbox suggestion is resolved and handed off (see
 * `~/lib/onboarding/host-address`) so sign in, onboarding, and the wizard's
 * location step can use it; without one, "List now" continues to onboarding
 * and the wizard asks for the address.
 */
export function HostAddressSearch({ tone = "light", className }: Props) {
  const router = useRouter();
  const id = useId();
  const listId = `${id}-suggestions`;

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<MapboxSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [picked, setPicked] = useState<MapboxSuggestion | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Created on first use rather than during render: with Cache Components,
  // `crypto.randomUUID()` while prerendering the page is an error.
  const sessionTokenRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only the latest request may apply its results, so a slow early response
  // cannot overwrite suggestions for what the visitor has typed since.
  const seqRef = useRef(0);
  // Bumped whenever the address changes, so a submit still waiting on Mapbox
  // can tell it was abandoned and must not store or navigate.
  const submitSeqRef = useRef(0);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  // Coming back to this page must not leave the button stuck loading. Next
  // keeps the page's state on a client-side Back (effects re-run when it is
  // shown again), and a bfcache restore keeps it too (only `pageshow` fires).
  useEffect(() => {
    setSubmitting(false);
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setSubmitting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const clear = () => {
    handleChange("");
    inputRef.current?.focus();
  };

  const handleChange = (value: string) => {
    submitSeqRef.current++;
    setSubmitting(false);
    setQuery(value);
    setPicked(null);
    setActiveIndex(-1);
    if (timerRef.current) clearTimeout(timerRef.current);

    if (value.trim().length < 3) {
      seqRef.current++;
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const seq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      sessionTokenRef.current ??= safeUUID();
      const results = await suggestAddresses(value, sessionTokenRef.current);
      if (seq !== seqRef.current) return;
      setSuggestions(results);
      setOpen(results.length > 0);
    }, 300);
  };

  const pick = (suggestion: MapboxSuggestion) => {
    setPicked(suggestion);
    setQuery(suggestion.full_address ?? suggestion.feature_name);
    setOpen(false);
    setActiveIndex(-1);
  };

  const goToOnboarding = () => {
    const code = new URLSearchParams(window.location.search).get(
      "attribution_code",
    );
    router.push(
      code
        ? `/host/onboarding?attribution_code=${encodeURIComponent(code)}`
        : "/host/onboarding",
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // The address is optional: without a picked suggestion (empty field,
    // nothing picked, or Mapbox returned nothing) continue to onboarding and
    // let the wizard collect it.
    if (!picked) {
      clearHostAddress();
      goToOnboarding();
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    const submitSeq = ++submitSeqRef.current;
    // Drop any address from an earlier pick in this tab, so a failed lookup
    // below can't hand the previous one to onboarding.
    clearHostAddress();
    // Resolve the suggestion to full address fields and coordinates. If that
    // fails or takes too long, the visitor still continues; they just enter
    // the address again in the wizard.
    sessionTokenRef.current ??= safeUUID();
    const feature = await Promise.race([
      retrieveAddress(picked.action.id, sessionTokenRef.current),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), RETRIEVE_TIMEOUT_MS),
      ),
    ]);
    if (submitSeq !== submitSeqRef.current) return;
    // Only a full street address carries over. A ZIP-only pick resolves with
    // the ZIP as its "street" and no postcode, which would prefill nonsense.
    if (feature?.streetAddress && feature.zip) writeHostAddress(feature);
    goToOnboarding();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      const active = suggestions[activeIndex];
      if (active) {
        e.preventDefault();
        pick(active);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className={cn("w-full max-w-[540px]", className)}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className={cn(
          "flex flex-col gap-2.5",
          "md:flex-row md:items-center md:gap-2 md:rounded-(--r-full) md:border md:border-(--ink-20) md:bg-white md:py-1.5 md:pr-1.5 md:pl-5 md:shadow-(--shadow-md)",
          "md:focus-within:border-(--blue) md:focus-within:ring-3 md:focus-within:ring-(--blue)/15",
          tone === "dark" && "md:border-transparent",
        )}
      >
        <div
          className={cn(
            "relative flex min-w-0 flex-1 items-center gap-2 rounded-(--r-full) border border-(--ink-20) bg-white px-4 py-1 shadow-(--shadow-md)",
            "focus-within:border-(--blue) focus-within:ring-3 focus-within:ring-(--blue)/15",
            "md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:focus-within:ring-0",
          )}
        >
          <MapPin className="size-[18px] shrink-0 text-(--blue)" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0 && !picked) setOpen(true);
            }}
            onBlur={() => setOpen(false)}
            placeholder="Address or ZIP"
            aria-label="Address or ZIP"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={
              activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
            }
            className="min-w-0 flex-1 bg-transparent px-1 py-3 font-ui text-base font-medium text-(--ink) outline-none placeholder:text-(--ink-60) md:py-2.5"
          />

          {query && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear address"
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-(--r-full) text-(--ink-40) hover:bg-(--ink-10) hover:text-(--ink) focus-visible:outline-2 focus-visible:outline-(--blue)"
            >
              <X className="size-4" aria-hidden />
            </button>
          )}

          {open && (
            <div
              id={listId}
              role="listbox"
              aria-label="Address suggestions"
              className="absolute top-full right-0 left-0 z-50 mt-2 max-h-72 overflow-y-auto rounded-(--r-lg) border border-(--ink-10) bg-white py-1 text-left shadow-(--shadow-lg) md:-left-5"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.mapbox_id}
                  id={`${listId}-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === activeIndex}
                  // mousedown, not click: it fires before the input's blur
                  // closes the list. onClick covers assistive tech that
                  // activates the option without a mouse.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(suggestion);
                  }}
                  onClick={() => pick(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={cn(
                    "flex w-full items-start gap-2.5 px-4 py-2.5 text-left font-ui text-[13px]",
                    index === activeIndex && "bg-(--blue-xpale)",
                  )}
                >
                  <MapPin
                    className="mt-0.5 size-3.5 shrink-0 text-(--blue)"
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-(--ink)">
                      {suggestion.feature_name}
                    </span>
                    {suggestion.description && (
                      <span className="block truncate text-xs text-(--ink-60)">
                        {suggestion.description}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          aria-busy={submitting || undefined}
          className="h-[52px] w-full md:h-12 md:w-auto"
        >
          List now
          {submitting ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <ArrowRight aria-hidden />
          )}
        </Button>
      </form>
    </div>
  );
}
